import React, { useState } from 'react';

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

const ComposeMessage = ({ segments, onSend }) => {
  const [selectedSegment, setSelectedSegment] = useState(segments[0]?.id || '');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const selectedSegmentData = segments.find(s => s.id === selectedSegment);

  const handleSend = async () => {
    if (!subject.trim() || !content.trim()) {
      setError('Please fill in both subject and message');
      return;
    }

    if (!selectedSegment) {
      setError('Please select a recipient segment');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const res = await fetch('/api/favorites/messages', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          segment: selectedSegment,
          subject: subject.trim(),
          content: content.trim()
        })
      });
      
      if (res.ok) {
        setSuccess(true);
        setSubject('');
        setContent('');
        if (onSend) onSend();
        setTimeout(() => setSuccess(false), 3000);
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="compose-message">
      <h4 className="text-sm font-medium text-gray-700 mb-4">✉️ Contact Your Favoriters</h4>
      <p className="text-sm text-gray-500 mb-4">
        Send a message to buyers who have favorited your store.
        Your message will be delivered through Our Pickup.
      </p>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Send to
          </label>
          <select
            value={selectedSegment}
            onChange={(e) => setSelectedSegment(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            {segments.map(segment => (
              <option key={segment.id} value={segment.id}>
                {segment.name} ({segment.count} recipients)
              </option>
            ))}
          </select>
          {selectedSegmentData && (
            <p className="text-xs text-gray-400 mt-1">
              {selectedSegmentData.count} buyers will receive this message
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Subject
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="e.g., New season harvest is here!"
            maxLength={200}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          <p className="text-xs text-gray-400 mt-1 text-right">{subject.length}/200</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Message
          </label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Your message here..."
            rows={5}
            maxLength={5000}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 resize-y"
          />
          <p className="text-xs text-gray-400 mt-1 text-right">{content.length}/5000</p>
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
            ❌ {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 text-green-700 p-3 rounded-lg text-sm">
            ✅ Message sent successfully!
          </div>
        )}

        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-400">
            All messages are sent through Our Pickup. Vendor contact details are not shared.
          </p>
          <button
            onClick={handleSend}
            disabled={loading || !subject.trim() || !content.trim()}
            className={`
              px-6 py-2 rounded-lg font-medium transition
              ${loading || !subject.trim() || !content.trim()
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
              }
            `}
          >
            {loading ? 'Sending...' : '✉️ Send Message'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ComposeMessage;
